<script>
import Error from '@/components/views/Error';
import Sidebar from '@/components/layouts/dashboard/Sidebar';

export default {
  name: 'DashboardLayout',
  components: {
    Error,
    Sidebar
  },
  props: {
    disableScrolling: {
      type: Boolean,
      default: false
    },
    loading: {
      type: Boolean,
      default: false
    },
    sidebar: {
      type: Boolean,
      default: true
    },
    size: {
      default: 'small',
      validator(value) {
        return ['small', 'medium', 'large', 'full'].includes(value);
      }
    },
    statusCode: {
      type: Number,
      default: 200
    }
  },
  computed: {
    isLoading() {
      return this.loading || this.statusCode === null;
    }
  }
};
</script>

<template>
  <div
    v-if="statusCode === 200 || statusCode === null"
    :class="{
      'dashboard-layout': true,
      'has-sidebar': sidebar,
      'is-scrolling-disabled': disableScrolling,
      'is-small': size === 'small',
      'is-medium': size === 'medium',
      'is-large': size === 'large',
      'is-full': size === 'full'
    }"
  >
    <!-- Sidebar -->
    <div v-if="sidebar" class="sidebar">
      <sidebar>
        <slot name="sidebar" />
      </sidebar>
    </div>

    <!-- Loader -->
    <div v-loading="isLoading" v-if="isLoading" class="loader" />

    <!-- Main -->
    <div v-if="!isLoading" class="main">
      <!-- Breadcrumbs -->
      <div
        v-if="$slots.breadcrumbs"
        :class="{
          'breadcrumbs': true
        }"
      >
        <slot name="breadcrumbs" />
      </div>

      <!-- Content -->
      <div class="content">
        <slot />
      </div>
    </div>
  </div>

  <!-- Error page -->
  <div v-else>
    <error :status-code="statusCode" />
  </div>
</template>

<style lang="scss" scoped>
@import '@/styles/variables.scss';

.dashboard-layout {
  width: 100%;
  min-height: 100vh;
}

// Loader
.loader {
  width: 100%;
  height: 100vh;
}

.has-sidebar .loader {
  width: calc(100% - #{$--dashboard-sidebar-width});
  height: 100vh;
  margin-left: $--dashboard-sidebar-width;

  @include breakpoint('sm-and-down') {
    width: 100%;
    height: calc(100vh - #{$--dashboard-sidebar-mobile-height});
    margin-left: 0;
  }
}

// Main container
.main {
  display: flex;
  width: 100%;
  min-height: 100vh;
  flex-wrap: nowrap;
  align-items: stretch;
  flex-direction: column;

  @include breakpoint('sm-and-down') {
    min-height: calc(100vh - #{$--dashboard-sidebar-mobile-height});
  }
}

.is-scrolling-disabled .main { // for views that handle their own scrolling (e.g. Site.vue)
  height: 100vh;
  min-height: 0;
  overflow: hidden;

  @include breakpoint('sm-and-down') {
    height: calc(100vh - #{$--dashboard-sidebar-mobile-height});
  }
}

.has-sidebar {
  .main {
    padding-left: $--dashboard-sidebar-width;

    @include breakpoint('sm-and-down') {
      padding-left: 0;
    }
  }
}

// Breadcrumbs
.breadcrumbs {
  flex: 0 0 auto;
  position: sticky;
  z-index: $--index-top;
  top: 0;
  background: $--color-white;
  border-bottom: solid 1px darken($--background-color-base, 7.5%);
  font-weight: 300;
  padding: .75rem 2rem;
  user-select: none;

  @include breakpoint('sm-and-down') {
    padding: .5rem 1rem;
  }

  /deep/ .el-breadcrumb {
    font-size: 1.1rem;
    line-height: 1.4;

    .el-breadcrumb__separator {
      font-size: 80%;
      margin-left: 4px;
      margin-right: 4px;
    }

    .el-breadcrumb__inner.is-link {
      color: $--color-primary;
      font-weight: 400;
      transition: .1s color;

      &:hover {
        color: darken($--color-primary, 10%);
      }
    }

    @include breakpoint('sm-and-down') {
      font-size: 1rem;
    }
  }
}

// Layouts
.content {
  padding-left: 2rem;
  padding-right: 2rem;
  margin: 0 auto;

  @include breakpoint('sm-and-down') {
    padding-left: 1rem;
    padding-right: 1rem;
  }
}

.is-small {
  .content {
    max-width: $--dashboard-content-sm-max-width;
    margin: 0 auto;
  }
}

.is-medium {
  .content {
    max-width: $--dashboard-content-md-max-width;
    margin: 0 auto;
  }
}

.is-large {
  .content {
    max-width: $--dashboard-content-lg-max-width;
    margin: 0 auto;
  }
}

.is-full {
  .content {
    max-width: 100%;
    padding: 0;
    margin: 0;
  }
}

// Content container
.content {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  width: 100%;
  min-height: 0; // required for scrolling child flex elements with overflow: auto in Firefox
  padding-top: 2rem;
  padding-bottom: 2rem;

  @include breakpoint('sm-and-down') {
    padding-top: 1rem;
    padding-bottom: 1rem;
  }
}
</style>
